
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { CartItem, Product } from '@/types';
import { toast } from '@/components/ui/use-toast';
import { trackAddToCart } from '@/utils/analytics';
import { trackFunnelEvent } from '@/utils/funnelTracker';

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (
    product: Product,
    selectedPotColor?: { name: string; color: string },
    selectedSize?: string,
    variantInfo?: { pot_type: string; pot_color: string; size: string },
    silent?: boolean,
    quantity?: number
  ) => void;
  removeFromCart: (cartItemId: string) => void;
  updateQuantity: (cartItemId: string, quantity: number) => void;
  clearCart: () => void;
  cartCount: number;
  cartTotal: number;
  isCartDrawerOpen: boolean;
  openCartDrawer: () => void;
  closeCartDrawer: () => void;
  setCartItemThumbnail: (cartItemId: string, thumbnail: { url: string; alt: string; width?: number; height?: number }) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try {
      const localData = localStorage.getItem('cartItems');
      return localData ? JSON.parse(localData) : [];
    } catch (error) {
      console.error("Could not parse cart items from localStorage", error);
      return [];
    }
  });
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  // Ensure absolute HTTPS URLs and prepare placeholders
  const toAbsoluteHttps = (url: string): string => {
    if (!url) return '';
    let u = url.trim();
    if (u.startsWith('//')) u = `https:${u}`;
    if (u.startsWith('http://')) u = u.replace('http://', 'https://');
    if (u.startsWith('/')) u = `${window.location.origin}${u}`;
    return u;
  };

  // One-time hydration for existing cart items lacking thumbnails
  useEffect(() => {
    setCartItems(prev => {
      let changed = false;
      const updated = prev.map(item => {
        const candidate = item.thumbnail?.url || item.image_urls?.[0] || '/placeholder.svg';
        const abs = toAbsoluteHttps(candidate);
        if (!item.thumbnail || item.thumbnail.url !== abs) {
          changed = true;
          return {
            ...item,
            thumbnail: { url: abs || toAbsoluteHttps('/placeholder.svg'), alt: item.name, width: 96, height: 96 },
          } as any;
        }
        return item;
      });
      return changed ? updated : prev;
    });
  }, []);

  const addToCart = (
    product: Product,
    selectedPotColor?: { name: string; color: string },
    selectedSize?: string,
    variantInfo?: { pot_type: string; pot_color: string; size: string },
    silent?: boolean,
    quantity?: number
  ) => {
    const qtyToAdd = Math.max(1, Number.isFinite(quantity as number) ? (quantity as number) : 1);

    const cartItemId = variantInfo
      ? `${product.id}-${variantInfo.pot_type.replace(/\s+/g, '-').toLowerCase()}-${variantInfo.size.replace(/\s+/g, '-').toLowerCase()}-${variantInfo.pot_color.replace(/\s+/g, '-').toLowerCase()}`
      : `${product.id}-${selectedPotColor?.name?.replace(/\s+/g, '-').toLowerCase() || 'no-color'}-${selectedSize?.replace(/\s+/g, '-').toLowerCase() || 'no-size'}`;

    const primaryUrl = product.image_urls?.[0] || '';
    const resolvedUrl = primaryUrl ? toAbsoluteHttps(primaryUrl) : toAbsoluteHttps('/placeholder.svg');
    const thumbnail = {
      url: resolvedUrl,
      alt: product.name,
      width: 96,
      height: 96,
    } as const;

    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.cartItemId === cartItemId);
      if (existingItem) {
        return prevItems.map(item =>
          item.cartItemId === cartItemId
            ? { ...item, quantity: item.quantity + qtyToAdd }
            : item
        );
      }
      const newItem: CartItem = {
        ...product,
        quantity: qtyToAdd,
        cartItemId,
        selectedPotColor,
        selectedSize,
        variantInfo,
        thumbnail,
      };
      return [...prevItems, newItem];
    });

    let productName = product.name;
    if (variantInfo) {
      productName += ` (${variantInfo.pot_type}, ${variantInfo.size}, ${variantInfo.pot_color})`;
    } else {
      if (selectedPotColor) productName += ` (${selectedPotColor.name})`;
      if (selectedSize) productName += ` - ${selectedSize}`;
    }

    // Track AddToCart event for Facebook Pixel
    trackAddToCart({
      content_name: product.name,
      content_ids: [product.id],
      content_type: 'product',
      value: product.price,
      currency: 'INR',
      quantity: qtyToAdd,
    });

    // Track funnel event
    trackFunnelEvent('add_to_cart', window.location.pathname, product.id);

    if (!silent) {
      toast({
        title: "Added to cart",
        description: `${productName} has been added to your cart.`,
      });
      setIsCartDrawerOpen(true);
    }
  };

  const removeFromCart = (cartItemId: string) => {
    setCartItems(prevItems => prevItems.filter(item => item.cartItemId !== cartItemId));
  };

  const updateQuantity = (cartItemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(cartItemId);
    } else {
      setCartItems(prevItems =>
        prevItems.map(item =>
          item.cartItemId === cartItemId ? { ...item, quantity } : item
        )
      );
    }
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const cartCount = cartItems.reduce((count, item) => count + item.quantity, 0);
  const cartTotal = cartItems.reduce((total, item) => total + item.price * item.quantity, 0);

  const openCartDrawer = () => setIsCartDrawerOpen(true);
  const closeCartDrawer = () => setIsCartDrawerOpen(false);

  const setCartItemThumbnail = (
    cartItemId: string,
    thumbnail: { url: string; alt: string; width?: number; height?: number }
  ) => {
    setCartItems(prev =>
      prev.map(item =>
        item.cartItemId === cartItemId ? { ...item, thumbnail } : item
      )
    );
  };

  return (
    <CartContext.Provider value={{ 
      cartItems, 
      addToCart, 
      removeFromCart, 
      updateQuantity, 
      clearCart, 
      cartCount, 
      cartTotal,
      isCartDrawerOpen,
      openCartDrawer,
      closeCartDrawer,
      setCartItemThumbnail,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
